import CompanyGoogleMap from "company/detail/components/CompanyGoogleMap";
import { DoorsEdit } from "company/detail/components/DoorsEdit";
import { PostalAndVisitingAddress } from "company/detail/components/models";
import { PostalAddressPreviewPanel } from "company/detail/components/PostalAddress";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { StageEdit } from "company/detail/components/StageEdit";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { convertToBankFormState, convertToFormState, convertToVenueFormState, getInstruction, getUpdateVenueInstuction } from "company/detail/utils";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import PhoneRow from "components/DetailPage/PhoneRow";
import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import VenueCategoryDropdown from "components/dropdowns/reactselect/VenueCategoryDropdown";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { PreviewPanelInPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import { PreviewPanelSectionTemplate } from "components/MainSectionTemplate/PreviewPanelSectionTemplate";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import Status from "components/Status/Status";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import WebsiteLink from "components/Website/WebsiteLink";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import Doors from "venue/components/Doors";
import Stage from "venue/components/Stage";
import { VenuePreviewPanelShowList } from "../PreviewPanelShowList";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { DeletedRow } from "components/DetailPage/DeletedRow";

interface VenuePreviewPanelContentProps {
  company: server.dto.Company
  venueDetails: server.dto.VenueDetails

  onChange: () => Promise<void>
}

export const VenuePreviewPanelContent = (props: VenuePreviewPanelContentProps) => {
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(props.company));
  const [venueFormState, setVenueFormState] = React.useState(convertToVenueFormState(props.venueDetails));
  const bankFormState = convertToBankFormState(props.company.bank);
  const [tags, setTags] = React.useState(props.company.tags || []);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "capacity") {
      setVenueFormState(prevState => ({
        ...prevState,
        capacity: value ?? 0,
      }));
    }
    else if (key === "ageRestriction") {
      setVenueFormState(prevState => ({
        ...prevState,
        ageRestriction: value,
      }));
    } else if (key === "doorsOpen") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          open: value,
        },
      }));
    } else if (key === "doorsClose") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          close: value,
        },
      }));
    } else if (key === "category") {
      setVenueFormState(prevState => ({
        ...prevState,
        category: value,
      }));
    } else if (key === "stage") {
      setVenueFormState(prevState => ({
        ...prevState,
        stage: value,
      }));
    } else if (key === "vehicleAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        vehicleAccess: value,
      }));
    } else if (key === "parking") {
      setVenueFormState(prevState => ({
        ...prevState,
        parking: value,
      }));
    } else if (key === "gearAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        gearAccess: value,
      }));
    } else if (key === "power") {
      setVenueFormState(prevState => ({
        ...prevState,
        power: value,
      }));
    } else if (key === "lighting") {
      setVenueFormState(prevState => ({
        ...prevState,
        lighting: value,
      }));
    } else if (key === "dressingRooms") {
      setVenueFormState(prevState => ({
        ...prevState,
        dressingRooms: value,
      }));
    } else if (key === "noiseRestrictions") {
      setVenueFormState(prevState => ({
        ...prevState,
        noiseRestrictions: value,
      }));
    } else if (key === "doors") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: value
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    }
  }

  const hasMultipleStatuses = site.currentEnvironment.statuses.company.length > 1;
  const hasAnyTags = useHasAnyTags("company");
  const hasLandlineNumbers = !isNullOrEmptyString(companyFormState.phone);
  const canInPageEdit = props.company.permissions.canModify;


  return <PreviewPanelSectionTemplate
    enabled={canInPageEdit}
    onSuccessfulUpdate={props.onChange}
    onValueChanged={onValueChanged}
  >
    <div className="flex-column gap-larger">
      {props.company.isDeleted && <PreviewPanelDetailRow>
        <DeletedRow />
      </PreviewPanelDetailRow>}
      {!props.company.isValidated && <PreviewPanelDetailRow>
        <NotValidatedRow canApprove={canInPageEdit} approveEndpoint={() => window.CoreApi.Company.Validate({
          companyId: props.company.companyId
        })} />
      </PreviewPanelDetailRow>}

      <PreviewPanelDetailRow labelTop={RESX.Company.VisitingAddress}>
        <PreviewPanelInPageEditableField
          field={{
            key: "visitingAddress",
            render: () => <>
              <div>{props.company.name}</div>
              <div className="text-sublabel">{props.company.address.formatted.join(", ")}</div>
            </>,
            inPageEdit: {
              useCustomSavingState: true,
              fullWidth: true,
              render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
                noLeftPadding
                submit={submit}
                onChange={onChange}
                value={value}
                isSaving={isSaving}
              />,
              initialValue: companyFormState.address,
              type: "custom",
              submitChange: (value: PostalAndVisitingAddress) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  regularName: value.visitingAddress.companyName,
                  regularAddress: {
                    addressLine1: value.visitingAddress.addressLine1,
                    addressLine2: value.visitingAddress.addressLine2,
                    city: value.visitingAddress.city,
                    postalCode: value.visitingAddress.postalCode,
                    stateId: value.visitingAddress.state.key,
                  },
                  administrativeName: value.postalAddress.companyName,
                  administrativeAddress: {
                    addressLine1: value.postalAddress.addressLine1,
                    addressLine2: value.postalAddress.addressLine2,
                    city: value.postalAddress.city,
                    postalCode: value.postalAddress.postalCode,
                    stateId: value.postalAddress.state.key,
                  }
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Company.PostalAddress}>
        <PreviewPanelInPageEditableField
          field={{
            key: "postalAddress",
            render: () => <PostalAddressPreviewPanel company={props.company} />,
            inPageEdit: {
              useCustomSavingState: true,
              fullWidth: true,
              render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
                noLeftPadding
                value={value}
                onChange={onChange}
                submit={submit}
                isSaving={isSaving}
              />,
              initialValue: companyFormState.address,
              type: "custom",
              submitChange: (value: PostalAndVisitingAddress) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  regularName: value.visitingAddress.companyName,
                  regularAddress: {
                    addressLine1: value.visitingAddress.addressLine1,
                    addressLine2: value.visitingAddress.addressLine2,
                    city: value.visitingAddress.city,
                    postalCode: value.visitingAddress.postalCode,
                    stateId: value.visitingAddress.state.key,
                  },
                  administrativeName: value.postalAddress.companyName,
                  administrativeAddress: {
                    addressLine1: value.postalAddress.addressLine1,
                    addressLine2: value.postalAddress.addressLine2,
                    city: value.postalAddress.city,
                    postalCode: value.postalAddress.postalCode,
                    stateId: value.postalAddress.state.key,
                  }
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Company.Phone}>
        <PreviewPanelInPageEditableField
          field={{
            key: "phone",
            render: () => <PhoneRow numbers={convertToPhoneNumbers(companyFormState.phone)} />,
            isEmpty: !hasLandlineNumbers,
            inPageEdit: {
              fullWidth: true,
              render: ({ value, onChange, submit, cancel }) => (
                <PhoneInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.phone,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  phoneNumbers: convertToPhoneNumbers(value),
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Website}>
        <PreviewPanelInPageEditableField
          field={{
            key: "website",
            render: () => <WebsiteLink website={companyFormState.website} />,
            isEmpty: !companyFormState.website,
            inPageEdit: {
              render: ({ value, onChange, cancel, submit }) => (
                <UrlInput
                  focusOnMount
                  value={value}
                  onValueChange={onChange}
                  validatorKey="companyWebsite"
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.website,
              fullWidth: true,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  website: value,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop="Facebook">
        <PreviewPanelInPageEditableField
          field={{
            key: "facebook",
            render: () => <WebsiteLink website={companyFormState.facebook} />,
            isEmpty: !companyFormState.facebook,
            inPageEdit: {
              render: ({ value, onChange, cancel, submit }) => (
                <UrlInput
                  focusOnMount
                  value={value}
                  onValueChange={onChange}
                  validatorKey="companyFacebook"
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.facebook,
              fullWidth: true,
              submitChange: (value: string) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  facebook: value,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Capacity}>
        <PreviewPanelInPageEditableField
          field={{
            key: "capacity",
            render: () => <div>{venueFormState.capacity} PAX</div>,
            isEmpty: !venueFormState.capacity,
            inPageEdit: {
              initialValue: venueFormState.capacity,
              render: ({ value, onChange, cancel }) => (
                <OptionalNumberInput
                  onEnterPressed={(e) => {
                    onChange(e, true);
                  }}
                  onEscPressed={cancel}
                  autoFocus
                  value={value}
                  onChange={onChange}
                  minValue={0}
                  maxValue={serverReference.constants.venue.maxCapacity}
                  canBeEmpty
                  decimalPoints={0}
                  labelContent="PAX"
                  width={100}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: number) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { capacity: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.AgeRestrictions}>
        <PreviewPanelInPageEditableField
          field={{
            key: "ageRestriction",
            render: () => <div>{venueFormState.ageRestriction.text}</div>,
            isEmpty: venueFormState.ageRestriction.value === "unknown",
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.ageRestriction,
              render: ({ value, onChange }) => (
                <AgeRestrictionDropdown
                  value={value}
                  onChange={onChange}
                  config={{
                    focusOnMount: true,
                    openMenuOnFocus: true,
                  }}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: server.dto.AgeRestriction) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { ageRestriction: value.value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Doors}>
        <PreviewPanelInPageEditableField
          field={{
            key: "doors",
            render: () => <Doors doors={venueFormState.doors} />,
            isEmpty: !venueFormState.doors.open && !venueFormState.doors.close,
            inPageEdit: {
              initialValue: venueFormState.doors,
              render: ({ value, onChange, }) => (
                <DoorsEdit
                  value={value}
                  onChange={onChange}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: server.dto.Doors) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, {
                  doors: {
                    open: value.open?.cultureInvariant,
                    close: value.close?.cultureInvariant
                  }
                });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Category}>
        <PreviewPanelInPageEditableField
          field={{
            key: "category",
            render: () => <div>{venueFormState.category.text || ""}</div>,
            isEmpty: venueFormState.category.value === "unknown",
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.category,
              render: ({ value, onChange }) => (
                <VenueCategoryDropdown
                  value={value}
                  onChange={onChange}
                  config={{
                    focusOnMount: true,
                    openMenuOnFocus: true,
                  }}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: server.dto.VenueCategory) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { category: value.value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Stage}>
        <PreviewPanelInPageEditableField
          field={{
            key: "stage",
            render: () => <Stage stage={venueFormState.stage} />,
            isEmpty: !venueFormState.stage.depth && !venueFormState.stage.width && !venueFormState.stage.toFloor && !venueFormState.stage.toCeiling,
            inPageEdit: {
              initialValue: venueFormState.stage,
              useCustomSavingState: true,
              fullWidth: true,
              render: ({ value, onChange, submit, isSaving }) => (
                <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
                  <StageEdit
                    stage={value}
                    onChange={onChange}
                    submit={submit}
                    isSaving={isSaving}
                  />
                </div>
              ),
              type: "custom",
              submitChange: async (value: server.dto.Stage) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { stage: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.VehicleAccess}>
        <PreviewPanelInPageEditableField
          field={{
            key: "vehicleAccess",
            render: () => <div>{venueFormState.vehicleAccess}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.vehicleAccess),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.vehicleAccess,
              render: ({ value, onChange, cancel, submit }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxVehicleAccessLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { vehicleAccess: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Parking}>
        <PreviewPanelInPageEditableField
          field={{
            key: "parking",
            render: () => <div>{venueFormState.parking}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.parking),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.parking,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxParkingLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { parking: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.GearAccess}>
        <PreviewPanelInPageEditableField
          field={{
            key: "gearAccess",
            render: () => <div>{venueFormState.gearAccess}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.gearAccess),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.gearAccess,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxGearAccessLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { gearAccess: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Power}>
        <PreviewPanelInPageEditableField
          field={{
            key: "power",
            render: () => <div>{venueFormState.power}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.power),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.power,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxPowerLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { power: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.Lighting}>
        <PreviewPanelInPageEditableField
          field={{
            key: "lighting",
            render: () => <div>{venueFormState.lighting}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.lighting),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.lighting,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxLightingLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { lighting: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.DressingRooms}>
        <PreviewPanelInPageEditableField
          field={{
            key: "dressingRooms",
            render: () => <div>{venueFormState.dressingRooms}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.dressingRooms),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.dressingRooms,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxDressingRoomsLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { dressingRooms: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Venue.NoiseRestrictions}>
        <PreviewPanelInPageEditableField
          field={{
            key: "noiseRestrictions",
            render: () => <div>{venueFormState.noiseRestrictions}</div>,
            isEmpty: isNullOrEmptyString(venueFormState.noiseRestrictions),
            inPageEdit: {
              fullWidth: true,
              initialValue: venueFormState.noiseRestrictions,
              render: ({ value, onChange, submit, cancel }) => (
                <OptionalTextInput
                  focusOnMount
                  value={value}
                  onChange={onChange}
                  maxLength={serverReference.constants.venue.maxNoiseRestrictionsLength}
                  onEnter={submit}
                  onEsc={cancel}
                />
              ),
              type: "confirmOrCancelButton",
              submitChange: async (value: string) => {
                const instruction = getUpdateVenueInstuction(props.company.companyId, venueFormState, { noiseRestrictions: value });
                return window.CoreApi.Venue.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
        <PreviewPanelInPageEditableField
          field={{
            key: "status",
            render: () => <Status status={companyFormState.status} />,
            hidden: !hasMultipleStatuses,
            inPageEdit: {
              render: ({ value, onChange }) => (
                <StatusDropdown
                  value={value}
                  onChange={onChange}
                  mainArea="company"
                  options={undefined}
                  config={{
                    focusOnMount: true,
                    openMenuOnFocus: true
                  }}
                />
              ),
              type: "confirmOrCancelButton",
              initialValue: companyFormState.status,
              fullWidth: true,
              submitChange: (value: server.dto.StatusSmall) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  statusId: value.statusId,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Owner}>
        <PreviewPanelInPageEditableField
          field={{
            key: "owner",
            render: () => <UserWithAvatar user={companyFormState.owner} />,
            isEmpty: !companyFormState.owner,
            inPageEdit: {
              render: ({ onChange }) => (
                <div className="main-section-template-fields-padding">
                  <UserModalPicker
                    value={undefined}
                    onChange={v => onChange(v, true)}
                    required={false}
                    validatorKey="companyOwner"
                    canCreateNew={false}
                  />
                </div>
              ),
              type: "custom",
              initialValue: companyFormState.owner,
              submitChange: (value: server.dto.UserSmall) => {
                const instruction = getInstruction(props.company, companyFormState, bankFormState, {
                  ownerUserId: value.userId,
                });

                return window.CoreApi.Company.UpdateDetails(instruction);
              }
            }
          }}
        />
      </PreviewPanelDetailRow>

      {hasAnyTags && <PreviewPanelDetailRow labelTop={RESX.Tag.Tags}>
        <PreviewPanelInPageEditableField
          field={{
            key: "tags",
            isEmpty: tags.length === 0,
            render: () => <TagList tags={tags} length="short" />,
            inPageEdit: {
              render: ({ value, onChange }) => (
                <div className="main-section-template-fields-padding">
                  <TagsSelect
                    mainArea="company"
                    value={value}
                    onChange={onChange}
                  />
                </div>
              ),
              type: "confirmOrCancelButton",
              initialValue: tags,
              submitChange: (tags: server.dto.TagSmall[]) => {
                return window.CoreApi.Company.UpdateTags({
                  itemId: props.company.companyId,
                  tagsIds: tags.map(tag => tag.tagId),
                });
              }
            }
          }}
        />
      </PreviewPanelDetailRow>}

      <ViewOnMap height={400}>
        <CompanyGoogleMap company={props.company} />
      </ViewOnMap>

      <VenuePreviewPanelShowList venueId={props.company.companyId} />
    </div>
  </PreviewPanelSectionTemplate>
}